import { Form, Radio } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { updateRecruiter } from '@/api/recruiter';
import { createRegion, setRecruiterLocations } from '@/api/regions';
import { createSeniority, setSeniorityList } from '@/api/seniority';
import {
  createSpecialization,
  setSpecializationList,
} from '@/api/specialization';
import FormItem from '@/components/form/FormItem';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { useCreatableTagField } from '@/components/form/shared/useCreatableTagField';
import { DASHBOARD } from '@/constants/routes';
import { useApiMutation } from '@/hooks/api';
import {
  useLocationReferenceData,
  useReferenceData,
} from '@/hooks/queries/useReferenceData';
import { useRefreshSessionUser } from '@/hooks/useRefreshSessionUser';
import { useUrlState } from '@/hooks/useUrlState';
import { tCatalog } from '@/i18n/tCatalog';
import { queryKeys } from '@/shared/lib/queryKeys';
import Loader from '@/UI/Loader';
import { PageTitle } from '@/UI/typography';
import { filterSelectOption, filterShowSearch } from '@/utils/selectHelpers';
import { buildRecruiterOnboardFormSchema } from '@/utils/validation/formSchemas/recruiterSchemas';

import type { SchemaSection } from '@/components/form/SchemaForm/types';
import type { Seniority, Specialization } from '@/types/reference';
import type { User } from '@/types/user';
import type { DefaultOptionType } from 'antd/es/select';

type RecruiterOnboardProps = {
  user: User;
  standalone?: boolean;
  isOnboard?: boolean;
  hideTitle?: boolean;
};

type RecruiterOnboardFormValues = {
  specializations: string[];
  seniority: string[];
  locations: string[];
  preference_relevance_interval: number;
};

function userSpecializations(user: User): Specialization[] {
  return user.specializations ?? [];
}

function userLocations(user: User): Array<{ id: string; name?: string }> {
  return user.locations ?? [];
}

function namedUserLocations(user: User): { id: string; name: string }[] {
  return userLocations(user).flatMap((row) =>
    row.name ? [{ id: row.id, name: row.name }] : [],
  );
}

function mergeById<T extends { id: string }>(primary: T[], extra: T[]): T[] {
  const ids = new Set(primary.map((row) => row.id));
  return [...primary, ...extra.filter((row) => !ids.has(row.id))];
}

function userSeniority(user: User): Seniority[] {
  if (!user.seniority || typeof user.seniority === 'string') {
    return [];
  }
  return user.seniority;
}

function userPreferenceInterval(user: User): number {
  return user.preference_relevance_interval ?? 0;
}

function collectSpecializationIds(specializations: Specialization[]): string[] {
  return specializations.flatMap((specialization) => [
    specialization.id,
    ...collectSpecializationIds(specialization.sub_domains ?? []),
  ]);
}

function buildSpecializationOptions(
  specializations: Specialization[],
): DefaultOptionType[] {
  return specializations.flatMap((specialization) => {
    const label = tCatalog('specializations', specialization.name);
    const selfOption = {
      value: specialization.id,
      label,
      key: specialization.id,
    };
    const children = specialization.sub_domains ?? [];
    if (children.length === 0) {
      return [selfOption];
    }
    return [
      selfOption,
      {
        label,
        key: `${specialization.id}-group`,
        options: children.map((child) => ({
          value: child.id,
          label: tCatalog('specializations', child.name),
          key: child.id,
        })),
      },
    ];
  });
}

function buildNamedOptions(
  section: 'seniority' | 'regions',
  records: { id: string; name: string }[],
): DefaultOptionType[] {
  return records.map((record) => ({
    value: record.id,
    label: tCatalog(section, record.name),
  }));
}

const RecruiterOnboard = ({
  user,
  standalone,
  isOnboard = false,
  hideTitle = false,
}: RecruiterOnboardProps) => {
  const refreshSessionUser = useRefreshSessionUser();
  const { t } = useTranslation();
  const { history } = useUrlState();
  const { data: locationReference, isLoading: locationReferenceLoading } =
    useLocationReferenceData();
  const { data: referenceData, isLoading: referenceDataLoading } =
    useReferenceData();
  const [form] = Form.useForm();
  const hydratedOnboardRef = useRef<string | null>(null);
  const formSchema = useMemo(() => buildRecruiterOnboardFormSchema(t), [t]);

  const specializations = useMemo(
    () =>
      mergeById(
        (referenceData?.domains ?? []).filter(
          (domain: Specialization) => domain.is_hidden === false,
        ),
        userSpecializations(user).map((row) => ({
          ...row,
          sub_domains: row.sub_domains ?? [],
        })),
      ),
    [referenceData, user],
  );
  const seniority = useMemo(
    () => mergeById(referenceData?.seniority ?? [], userSeniority(user)),
    [referenceData, user],
  );
  const regions = useMemo(
    () => mergeById(locationReference?.regions ?? [], namedUserLocations(user)),
    [locationReference, user],
  );

  const seniorityToOptions = useCallback(
    (records: Seniority[]) => buildNamedOptions('seniority', records),
    [],
  );
  const locationToOptions = useCallback(
    (records: { id: string; name: string }[]) =>
      buildNamedOptions('regions', records),
    [],
  );

  const createSpecializationRecord = useCallback(async (name: string) => {
    const created = await createSpecialization(name);
    return {
      ...created,
      sub_domains: created.sub_domains ?? [],
    };
  }, []);
  const localSpecialization = useCallback(
    (name: string, localId: string): Specialization => ({
      id: localId,
      name,
      sub_domains: [],
    }),
    [],
  );

  const specializationField = useCreatableTagField({
    form,
    fieldName: 'specializations',
    records: specializations,
    create: createSpecializationRecord,
    toLocalRecord: localSpecialization,
    toOptions: buildSpecializationOptions,
    getKnownIds: collectSpecializationIds,
  });
  const seniorityField = useCreatableTagField({
    form,
    fieldName: 'seniority',
    records: seniority,
    create: createSeniority,
    toOptions: seniorityToOptions,
  });
  const locationField = useCreatableTagField({
    form,
    fieldName: 'locations',
    records: regions,
    create: createRegion,
    toOptions: locationToOptions,
  });

  const preferences = useMemo(
    () => [
      { name: `1 ${t('MONTH')}`, value: 1 },
      { name: `2 ${t('MONTHS')}`, value: 2 },
      {
        name: `6 ${t('MONTHS')}`,
        value: 6,
      },
      { name: `12 ${t('MONTHS')}`, value: 12 },
    ],
    [t],
  );

  const tagSelectProps = useMemo(
    () => ({
      mode: 'multiple' as const,
      virtual: false,
      allowClear: true,
      maxTagCount: 'responsive' as const,
      showSearch: filterShowSearch(filterSelectOption),
      placeholder: t('SELECT_OR_ADD_PLACEHOLDER'),
    }),
    [t],
  );

  const isCreatingTags =
    specializationField.isCreating ||
    seniorityField.isCreating ||
    locationField.isCreating;

  const onboardMutation = useApiMutation({
    mutationFn: async (values: RecruiterOnboardFormValues) => {
      const specializations = await specializationField.commit(
        values.specializations,
      );
      const seniority = await seniorityField.commit(values.seniority);
      const locations = await locationField.commit(values.locations);
      await Promise.all([
        setSpecializationList(specializations),
        setSeniorityList(seniority),
        setRecruiterLocations(locations),
        updateRecruiter({
          preference_relevance_interval: values.preference_relevance_interval,
        }),
      ]);
    },
    successKey: 'INFORMATION_SAVE_SUCCESS',
    invalidateKeys: [queryKeys.user.recruiterInfo(), queryKeys.reference.all],
    onSuccess: async () => {
      await refreshSessionUser();
      if (!standalone) {
        history.push(DASHBOARD);
      }
    },
  });
  const loading = onboardMutation.loading;

  const onFinish = (values: RecruiterOnboardFormValues) => {
    if (isCreatingTags) {
      return;
    }
    onboardMutation.mutate(values);
  };

  useEffect(() => {
    if (!locationReference || !referenceData) return;

    const identityKey = `${user?.id ?? 'anon'}-${isOnboard ? 'onboard' : 'edit'}`;
    if (hydratedOnboardRef.current === identityKey) return;
    hydratedOnboardRef.current = identityKey;

    const locationList = locationReference.regions;
    const seniorityList = referenceData.seniority;
    const filteredSpecsForOnboard = referenceData.domains.filter(
      (domain: Specialization) => domain.is_hidden === false,
    );

    if (isOnboard) {
      form.setFieldsValue({
        locations: locationList.map((item) => item.id),
        seniority: seniorityList.map((item) => item.id),
        specializations: collectSpecializationIds(filteredSpecsForOnboard),
        preference_relevance_interval: userPreferenceInterval(user),
      });
      return;
    }

    form.setFieldsValue({
      locations: userLocations(user).map((item) => item.id),
      seniority: userSeniority(user).map((item) => item.id),
      specializations: userSpecializations(user).map((item) => item.id),
      preference_relevance_interval: userPreferenceInterval(user),
    });
  }, [locationReference, referenceData, user, form, isOnboard]);

  const pageLoad = locationReferenceLoading || referenceDataLoading;

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'specialism',
        title: t('SPECIALIZATIONS_LABEL'),
        fields: [
          {
            name: 'specializations',
            type: 'select',
            componentProps: {
              ...tagSelectProps,
              options: specializationField.options,
              onChange: specializationField.onChange,
              onInputKeyDown: specializationField.onInputKeyDown,
              onSearch: specializationField.onSearch,
              searchValue: specializationField.searchValue,
              loading: specializationField.isCreating,
              'data-testid': 'recruiter-onboard-specializations',
            },
          },
        ],
      },
      {
        key: 'seniority',
        title: t('SENIORITY_LABEL'),
        fields: [
          {
            name: 'seniority',
            type: 'select',
            componentProps: {
              ...tagSelectProps,
              options: seniorityField.options,
              onChange: seniorityField.onChange,
              onInputKeyDown: seniorityField.onInputKeyDown,
              onSearch: seniorityField.onSearch,
              searchValue: seniorityField.searchValue,
              loading: seniorityField.isCreating,
              'data-testid': 'recruiter-onboard-seniority',
            },
          },
        ],
      },
      {
        key: 'locations',
        title: t('LOCATIONS'),
        fields: [
          {
            name: 'locations',
            type: 'select',
            componentProps: {
              ...tagSelectProps,
              options: locationField.options,
              onChange: locationField.onChange,
              onInputKeyDown: locationField.onInputKeyDown,
              onSearch: locationField.onSearch,
              searchValue: locationField.searchValue,
              loading: locationField.isCreating,
              'data-testid': 'recruiter-onboard-locations',
            },
          },
        ],
      },
      {
        key: 'preferences',
        title: t('PREFERENCES_LABEL'),
        fields: [
          {
            key: 'preferences-field',
            type: 'custom',
            render: () => (
              <FormItem name="preference_relevance_interval">
                <Radio.Group optionType="button" buttonStyle="solid" block>
                  {preferences.map((item) => (
                    <Radio.Button key={item.value} value={item.value}>
                      {item.name}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </FormItem>
            ),
          },
        ],
      },
    ],
    [
      t,
      tagSelectProps,
      specializationField.options,
      specializationField.onChange,
      specializationField.onInputKeyDown,
      specializationField.onSearch,
      specializationField.searchValue,
      specializationField.isCreating,
      seniorityField.options,
      seniorityField.onChange,
      seniorityField.onInputKeyDown,
      seniorityField.onSearch,
      seniorityField.searchValue,
      seniorityField.isCreating,
      locationField.options,
      locationField.onChange,
      locationField.onInputKeyDown,
      locationField.onSearch,
      locationField.searchValue,
      locationField.isCreating,
      preferences,
    ],
  );

  return pageLoad ? (
    <Loader spinning={pageLoad} />
  ) : (
    <SchemaForm
      scrollToFirstError
      layout="vertical"
      name="client-onboard"
      form={form}
      formSchema={formSchema}
      onFinish={onFinish}
      sections={sections}
      footer={{
        actions: [
          {
            label: !standalone ? t('NEXT') : t('SAVE_TITLE'),
            htmlType: 'submit',
            type: 'primary',
            loading,
            disabled: isCreatingTags,
          },
        ],
      }}
    >
      {!hideTitle && <PageTitle>{t('CANDIDATE_TITLE')}</PageTitle>}
    </SchemaForm>
  );
};

export default RecruiterOnboard;
